import { ActionButtons, Column, Modal, Row } from "../../../../widget";
import { TaskParentKind } from "../../taskParentPolicy";

interface RemoveAttachmentModalProps {
  show: boolean;
  /** The file being detached, named in the prompt so the user is not asked about "an attachment". */
  fileName: string | undefined;
  /*
    The store the document stays in, which the prompt names. Undefined only for a parent that cannot hold
    documents — which has no attachments to remove — so the wording falls back to the neutral phrasing.
  */
  parentKind: TaskParentKind | undefined;
  isLoading: boolean;
  closeModal: () => void;
  onConfirm: () => void;
}

// Where a detached document remains, phrased for the prompt.
const PARENT_DOCUMENT_STORE: Record<TaskParentKind, string> = {
  project: "the project's documents",
  deal: "the deal's documents",
  landholding: "the landholding's documents",
};

/*
  Confirms detaching a document from a task. Worth a prompt even though nothing is destroyed: the trash icon
  reads as a delete, so the prompt is mostly there to say what it does not do — the document itself is left
  where it is (the server soft-deletes only the object_document link, BR-02813).
*/
export const RemoveAttachmentModal = ({
  show,
  fileName,
  parentKind,
  isLoading,
  closeModal,
  onConfirm,
}: RemoveAttachmentModalProps): JSX.Element => {
  const documentStore = parentKind ? PARENT_DOCUMENT_STORE[parentKind] : "its document store";

  return (
    <Modal show={show} title="Remove attachment?" onClose={closeModal}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm();
        }}
      >
        <Row spacingV="ll" justify="center">
          <Column span={10}>
            <p className="body1">
              {fileName
                ? `${fileName} will no longer be attached to this task. `
                : "This document will no longer be attached to this task. "}
              The document itself is not deleted, it stays in {documentStore}.
            </p>
          </Column>
        </Row>
        <Row justify="center">
          <ActionButtons onCancel={closeModal} submitButtonLabel="Remove" isLoading={isLoading} />
        </Row>
      </form>
    </Modal>
  );
};
